import {
  CodeBlock,
  CodeBlockCopyButton,
} from "@/components/ai-elements/code-block";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { ReloadIcon } from "@radix-ui/react-icons";
import { CompanyDTO } from "@shaarp/common";
import { CheckIcon, ClipboardIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type EnrichmentCacheEntry = {
  provider: string;
  cacheKey: string;
  data: unknown | null;
};

type CompanyDebugModalProps = {
  company: CompanyDTO;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export default function CompanyDebugModal({
  company,
  open,
  onOpenChange,
}: CompanyDebugModalProps) {
  const { t } = useTranslation();
  const api = useApi();

  const [enrichmentCache, setEnrichmentCache] = useState<
    EnrichmentCacheEntry[] | null
  >(null);
  const [isLoadingCache, setIsLoadingCache] = useState(false);
  const [isCopiedAll, setIsCopiedAll] = useState(false);
  const [activeDebugTab, setActiveDebugTab] = useState("company-dto");

  const copyAllAsPrompt = useCallback(() => {
    const sections: string[] = [
      `# Company: ${company.name}`,
      "",
      "## CompanyDTO",
      "```json",
      JSON.stringify(company, null, 2),
      "```",
    ];

    if (enrichmentCache) {
      for (const entry of enrichmentCache) {
        sections.push("");
        sections.push(`## ${entry.provider}`);
        if (entry.data) {
          sections.push("```json");
          sections.push(JSON.stringify(entry.data, null, 2));
          sections.push("```");
        } else {
          sections.push("*No cached data*");
        }
      }
    }

    navigator.clipboard.writeText(sections.join("\n")).then(() => {
      setIsCopiedAll(true);
      setTimeout(() => setIsCopiedAll(false), 2000);
    });
  }, [company, enrichmentCache]);

  useEffect(() => {
    if (open && company.id) {
      setIsLoadingCache(true);
      setActiveDebugTab("company-dto");
      api
        .get(`/company/${company.id}/debug/enrichment-cache`)
        .then((res) => setEnrichmentCache(res.data))
        .catch(() => setEnrichmentCache(null))
        .finally(() => setIsLoadingCache(false));
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[90vw] w-full max-h-[85vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="truncate">
            {company.name} — {t("company.debug.title")}
          </DialogTitle>
        </DialogHeader>
        <div className="flex gap-4 min-h-0">
          <div className="flex flex-col shrink-0 w-52 max-h-[70vh]">
            <div className="overflow-y-auto flex-1">
              <div className="flex flex-col gap-1 items-stretch p-1 rounded-lg bg-muted/50">
                <button
                  type="button"
                  className={cn(
                    "inline-flex items-center justify-start whitespace-nowrap rounded-md px-3 py-1 text-xs font-medium transition-all",
                    activeDebugTab === "company-dto"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-muted/60",
                  )}
                  onClick={() => setActiveDebugTab("company-dto")}
                >
                  CompanyDTO
                </button>
                {isLoadingCache && (
                  <div className="flex justify-center items-center py-2">
                    <ReloadIcon className="w-3.5 h-3.5 animate-spin" />
                  </div>
                )}
                {enrichmentCache?.map((entry) => (
                  <button
                    type="button"
                    key={entry.provider}
                    className={cn(
                      "inline-flex items-center justify-start whitespace-nowrap rounded-md px-3 py-1 text-xs font-medium transition-all",
                      activeDebugTab === entry.provider
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:bg-muted/60",
                      !entry.data &&
                        activeDebugTab !== entry.provider &&
                        "opacity-40",
                    )}
                    onClick={() => setActiveDebugTab(entry.provider)}
                  >
                    <span
                      className={cn(
                        "inline-block mr-2 w-2 h-2 rounded-full shrink-0",
                        entry.data ? "bg-green-500" : "bg-gray-300",
                      )}
                    />
                    {entry.provider}
                  </button>
                ))}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 w-full"
              onClick={copyAllAsPrompt}
              disabled={isLoadingCache}
            >
              {isCopiedAll ? (
                <CheckIcon className="mr-1.5 w-3.5 h-3.5 text-green-500" />
              ) : (
                <ClipboardIcon className="mr-1.5 w-3.5 h-3.5" />
              )}
              {isCopiedAll
                ? t("company.debug.copied")
                : t("company.debug.copyAllAsPrompt")}
            </Button>
          </div>
          <div className="overflow-hidden flex-grow w-0">
            {activeDebugTab === "company-dto" && (
              <div className="overflow-auto max-h-[70vh]">
                <CodeBlock
                  code={JSON.stringify(company, null, 2)}
                  language="json"
                >
                  <CodeBlockCopyButton />
                </CodeBlock>
              </div>
            )}
            {enrichmentCache?.map((entry) =>
              activeDebugTab === entry.provider ? (
                <div key={entry.provider}>
                  <div className="overflow-auto max-h-[70vh]">
                    {entry.data ? (
                      <CodeBlock
                        code={JSON.stringify(entry.data, null, 2)}
                        language="json"
                      >
                        <CodeBlockCopyButton />
                      </CodeBlock>
                    ) : (
                      <p className="py-8 text-center text-muted-foreground">
                        {t("company.debug.noCachedData")}
                      </p>
                    )}
                  </div>
                  <p className="mt-2 font-mono text-xs truncate text-muted-foreground">
                    Cache key: {entry.cacheKey}
                  </p>
                </div>
              ) : null,
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
